import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import LockedScreen from "@/pages/Locked";
import Account from "@/pages/Account";

import ManagerLayout from "@/components/app/ManagerLayout";
import Dashboard from "@/pages/manager/Dashboard";
import Workers from "@/pages/manager/Workers";
import Tools from "@/pages/manager/Tools";
import QRLabels from "@/pages/manager/QRLabels";
import Exposures from "@/pages/manager/Exposures";
import CheckIns from "@/pages/manager/CheckIns";
import Reports from "@/pages/manager/Reports";
import SafetyCulture from "@/pages/manager/SafetyCulture";
import Branding from "@/pages/manager/Branding";

import WorkerLayout from "@/components/app/WorkerLayout";
import SelectWorker from "@/pages/worker/SelectWorker";
import Scan from "@/pages/worker/Scan";
import Timer from "@/pages/worker/Timer";
import ManualEntry from "@/pages/worker/ManualEntry";
import WorkerCheckIn from "@/pages/worker/WorkerCheckIn";

const MANAGER_ROLES = ["manager"];
const WORKER_ROLES = ["worker", "manager"];

function Protected({ roles, children }) {
  const { user, loading, accountStatus } = useAuth();
  if (loading) return <div className="p-10 text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={user.role === "manager" ? "/dashboard" : "/worker"} replace />;
  }
  if (accountStatus && accountStatus.active === false) {
    return <Navigate to="/locked" replace />;
  }
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/signup" replace />;
  return <Navigate to={user.role === "manager" ? "/dashboard" : "/worker"} replace />;
}

function LockedRoute() {
  const { accountStatus, logout } = useAuth();
  const navigate = useNavigate();

  const onDelete = async () => {
    const target = "DELETE COMPANY";
    const confirm = window.prompt(
      `This permanently deletes your company and ALL data (employees, tools, exposures, check-ins, users). This cannot be undone.\n\nType ${target} to confirm.`
    );
    if (confirm?.trim().toUpperCase() !== target) return;
    try {
      await api.delete("/account/me");
      toast.success("Account deleted");
      await logout();
      navigate("/signup", { replace: true });
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Delete failed");
    }
  };

  return <LockedScreen status={accountStatus} onDelete={onDelete} />;
}

function LockedGate() {
  const { user, accountStatus, loading } = useAuth();
  if (loading) return <div className="p-10 text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // If the account is active, send them home — /locked is only meaningful when expired.
  if (accountStatus?.active) {
    return <Navigate to={user.role === "manager" ? "/dashboard" : "/worker"} replace />;
  }
  return <LockedRoute />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/locked" element={<LockedGate />} />

          <Route
            element={
              <Protected roles={MANAGER_ROLES}>
                <ManagerLayout />
              </Protected>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/workers" element={<Workers />} />
            <Route path="/tools" element={<Tools />} />
            <Route path="/qr-labels" element={<QRLabels />} />
            <Route path="/exposures" element={<Exposures />} />
            <Route path="/checkins" element={<CheckIns />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/safetyculture" element={<SafetyCulture />} />
            <Route path="/branding" element={<Branding />} />
            <Route path="/account" element={<Account />} />
          </Route>

          <Route
            element={
              <Protected roles={WORKER_ROLES}>
                <WorkerLayout />
              </Protected>
            }
          >
            <Route path="/worker" element={<SelectWorker />} />
            <Route path="/worker/scan" element={<Scan />} />
            <Route path="/worker/manual" element={<ManualEntry />} />
            <Route path="/worker/timer/:toolCode" element={<Timer />} />
            <Route path="/worker/checkin" element={<WorkerCheckIn />} />
            <Route path="/worker/account" element={<Account />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
